import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Output,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { GoogleAuthService } from '../../../shared/services/google-auth.service';
import { FacebookAuthService } from '../../../shared/services/facebook-auth.service';

declare const google: any;

/**
 * Renders the "Continue with Google" / "Continue with Facebook" buttons and
 * handles the SDK plumbing for both providers, so every auth form (login,
 * register, merchant register) shares one implementation instead of copy-pasted
 * static markup.
 *
 * Google: we render Google's own button invisibly on top of our custom-styled
 * button (an overlay). Clicking anywhere on our button therefore always
 * triggers Google's real, native account chooser — this is what fixes the old
 * "sometimes works, sometimes doesn't" One Tap prompt() behaviour, which Google
 * is allowed to silently skip (e.g. after a prior dismissal, in some browsers,
 * or when third-party cookies are restricted).
 *
 * Facebook: FB.login() opens Facebook's own login/account dialog and returns a
 * short-lived access token, which the parent form forwards to the backend.
 */
@Component({
  selector: 'app-social-login-buttons',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './social-login-buttons.html',
})
export class SocialLoginButtonsComponent implements AfterViewInit {
  @Output() googleCredential = new EventEmitter<string>();
  @Output() facebookAccessToken = new EventEmitter<string>();

  @ViewChild('googleVisualBtn') googleVisualBtn?: ElementRef<HTMLDivElement>;
  @ViewChild('googleRealBtn') googleRealBtn?: ElementRef<HTMLDivElement>;

  isFacebookLoading = false;

  private platformId = inject(PLATFORM_ID);

  constructor(
    private googleAuth: GoogleAuthService,
    private facebookAuth: FacebookAuthService,
    private toastr: ToastrService
  ) {}

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      await this.googleAuth.waitForSdk();

      google.accounts.id.initialize({
        client_id: environment.googleClientId,
        callback: (response: any) => this.googleCredential.emit(response.credential),
        auto_select: false,
        use_fedcm_for_prompt: true,
      });

      const measuredWidth = this.googleVisualBtn?.nativeElement?.offsetWidth ?? 240;
      const width = Math.min(Math.max(measuredWidth, 200), 400);

      if (this.googleRealBtn) {
        google.accounts.id.renderButton(this.googleRealBtn.nativeElement, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          logo_alignment: 'left',
          width,
        });
      }
    } catch (err) {
      // SDK failed to load (network issue / blocked script). Leave the visual
      // button in place but it just won't be clickable — no crash either way.
      console.error('Google Sign-In could not be initialized:', err);
    }
  }

  async signInWithFacebook(): Promise<void> {
    if (this.isFacebookLoading) return;
    this.isFacebookLoading = true;

    try {
      const accessToken = await this.facebookAuth.login();
      this.facebookAccessToken.emit(accessToken);
    } catch (err: any) {
      if (err?.message !== 'cancelled') {
        this.toastr.error('Facebook login failed. Please try again.', 'Error');
      }
    } finally {
      this.isFacebookLoading = false;
    }
  }
}
